// src/components/TendersPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import Header from "./Header";
import Footer from "./Footer";
import directus from "../lib/directus";
import { readItems } from '@directus/sdk';

// 动态计算招标状态
const getTenderStatus = (deadlineStr) => {
  if (!deadlineStr) return "进行中";
  const now = new Date();
  const deadline = new Date(deadlineStr);
  if (isNaN(deadline.getTime())) return "进行中";
  if (now > deadline) return "已截止";
  const diffTime = deadline - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays <= 3) return "即将截止";
  return "进行中";
};

// 状态颜色映射
const getStatusColor = (status) => {
  switch (status) {
    case "已截止": return "bg-gray-100 text-gray-800";
    case "即将截止": return "bg-orange-100 text-orange-800";
    default: return "bg-blue-100 text-blue-800";
  }
};

const TendersPage = ({ setCurrentPage }) => {
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
     window.scrollTo({ top: 0, behavior: 'smooth' }); 

    const fetchTenders = async () => {
      try {
        const response = await directus.request(
          readItems('tenders', {
            fields: [
              'id',
              'title',
              'number',
              'type',
              'publish_date',
              'deadline',
              'budget',
              'location',
              'description'
            ],
            sort: ['-publish_date'],
            limit: -1
          })
        );
        setTenders(Array.isArray(response) ? response : []);
      } catch (err) {
        console.error("加载招标信息失败:", err);
        setError("加载招标信息失败，请稍后重试");
      } finally {
        setLoading(false);
      }
    };

    fetchTenders();
  }, []);

  const tendersWithStatus = useMemo(() => {
    return tenders.map(t => ({
      ...t,
      status: getTenderStatus(t.deadline)
    }));
  }, [tenders]);

  return (
    <div>
      <Header currentPage="tenders" setCurrentPage={setCurrentPage} />
      <main className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">招标信息公示</h2>
            <div className="w-40 h-1 bg-gradient-to-r from-blue-600 via-purple-600 to-emerald-600 mx-auto"></div>
            <p className="mt-4 text-xl text-gray-600 max-w-4xl mx-auto">
              公开、公平、公正的招标信息平台，欢迎符合条件的企业参与投标。
            </p>
          </div>

          {/* 左右分栏布局 */}
          <div className="grid lg:grid-cols-3 gap-12">
            {/* 左侧：投标咨询（1/3） */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-3xl p-6 border border-blue-100">
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">投标咨询</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">电子邮箱</span>
                    <span className="font-bold text-blue-600">452081898@qq.com</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">办公地址</span>
                    <span className="font-bold text-gray-900">昆明市西山区秋苑二期</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-blue-200 text-xs text-gray-600">
                  <p>• 投标文件递交截止时间：招标截止日期当日17:00前</p>
                  <p>• 开标时间：招标截止日期次日10:00</p>
                </div>
              </div>
            </div>

            {/* 右侧：招标列表（2/3） */}
            <div className="lg:col-span-2 space-y-8">
              {/* 最新招标公告 Banner */}
              <div className="bg-gradient-to-r from-blue-500 to-emerald-600 rounded-3xl p-6 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-black opacity-20"></div>
                <div className="relative z-10">
                  <h3 className="text-xl font-bold">最新招标公告</h3>
                  <p className="text-blue-100 text-sm">请及时关注招标截止日期，把握合作机会</p>
                </div>
              </div>

              {/* 招标列表 */}
              {error ? (
                <div className="text-center text-red-600 py-8">{error}</div>
              ) : tendersWithStatus.length === 0 ? (
                <div className="text-center text-gray-600 py-8">暂无招标信息</div>
              ) : (
                tendersWithStatus.map((tender) => (
                  <div
                    key={tender.id}
                    className="bg-white border border-gray-200 rounded-3xl p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <h3 className="text-lg font-bold text-gray-900">{tender.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(tender.status)}`}>
                        {tender.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
                      <div><span className="text-gray-500">编号：</span>{tender.number}</div>
                      <div><span className="text-gray-500">类型：</span>{tender.type}</div>
                      <div><span className="text-gray-500">发布：</span>{tender.publish_date}</div>
                      <div>
                        <span className="text-gray-500">截止：</span>
                        <span className={tender.status === "即将截止" ? "text-red-600 font-bold" : ""}>
                          {tender.deadline}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                      <div><span className="text-gray-500">预算：</span>{tender.budget}</div>
                      <div><span className="text-gray-500">地点：</span>{tender.location}</div>
                    </div>

                    {/* 仅保留招标内容 */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h4 className="text-xs font-semibold text-gray-900 mb-1">招标内容</h4>
                      <p className="text-gray-700 text-sm">{tender.description}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer currentPage="tenders" setCurrentPage={setCurrentPage} />
    </div>
  );
};

export default TendersPage;